import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../layouts/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import TimelineMetric from '../../components/ui/TimelineMetric';
import {
  FileText, Calendar, TrendingUp, AlertCircle,
  MessageSquare, Clock, CheckCircle
} from 'lucide-react';
import dashboardIcon from '../../assets/img3d/dashboard.png';
import chatIcon from '../../assets/img3d/chat.png';
import calendarIcon from '../../assets/img3d/calendar.png';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    today_requests: 0,
    in_progress: 0,
    week_visits: 0,
    month_conversions: 0,
    urgent_actions: {
      unassigned: 0,
      pending_pro_response: 0,
      unread_messages: 0
    },
    pipeline: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/stats`);
      const data = response.data;
      
      // Transform camelCase API response to snake_case format expected by frontend
      setStats({
        today_requests: data.todayRequests || 0,
        in_progress: data.inProgress || 0,
        week_visits: data.weekVisits || 0,
        month_conversions: data.monthConversions || 0,
        urgent_actions: {
          unassigned: data.urgentActions?.unassigned || 0,
          pending_pro_response: data.urgentActions?.pendingProResponse || 0,
          unread_messages: data.urgentActions?.unreadMessages || 0,
        },
        pipeline: data.pipeline || {},
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Keep default state on error to prevent undefined access
    } finally {
      setLoading(false);
    }
  };

  const getPipelineCount = (status) => {
    return stats.pipeline[status] || 0;
  };

  return (
    <Layout
      headerConfig={{
        icon: dashboardIcon,
        iconColor: 'bg-white',
        title: 'Dashboard Admin',
        subtitle: `Bienvenue ${user?.first_name} - Vue d'ensemble de la plateforme`,
        primaryAction: undefined,
        secondaryAction: undefined,
        tertiaryAction: undefined,
        onPrimaryAction: undefined,
        onSecondaryAction: undefined,
        onTertiaryAction: undefined
      }}
    >
      {/* Top KPIs - Timeline Metrics */}
      <div className="mb-8 w-full">
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden w-full">
          <div className="flex flex-col md:flex-row w-full">
            <div className="flex-1">
              <TimelineMetric
                icon={FileText}
                value={stats.today_requests}
                label="Demandes Aujourd'hui"
                type="properties"
                onClick={() => navigate('/admin/contact-requests')}
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={Clock}
                value={stats.in_progress}
                label="En Cours"
                type="arrears"
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={Calendar}
                value={stats.week_visits}
                label="Visites Semaine"
                type="occupancy"
                onClick={stats.week_visits > 0 ? () => navigate('/admin/visits') : undefined}
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={TrendingUp}
                value={stats.month_conversions}
                label="Conversions Mois"
                type="owners"
                noBorder={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Urgent Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Actions Urgentes</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/admin/contact-requests?filter=unassigned')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <span className="font-semibold">Demandes Non Assignées</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">
                    {stats.urgent_actions.unassigned}
                  </p>
                </div>
                <Badge variant="destructive">Urgent</Badge>
              </div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/admin/contact-requests?filter=pending_pro')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Clock className="h-5 w-5 text-orange-600" />
                    <span className="font-semibold">En Attente Pro</span>
                  </div>
                  <p className="text-2xl font-bold text-orange-600">
                    {stats.urgent_actions.pending_pro_response}
                  </p>
                </div>
                <Badge className="bg-orange-100 text-orange-800">À Suivre</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <MessageSquare className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold">Messages Non Lus</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">
                    {stats.urgent_actions.unread_messages}
                  </p>
                </div>
                <Badge className="bg-blue-100 text-blue-800">Nouveau</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pipeline Overview */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Pipeline des Demandes</h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div
              className="text-center p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => navigate('/admin/contact-requests?status=new')}
            >
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {getPipelineCount('new')}
              </div>
              <div className="text-sm text-gray-600">Nouveau</div>
            </div>

            <div
              className="text-center p-4 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => navigate('/admin/contact-requests?status=qualified')}
            >
              <div className="text-3xl font-bold text-blue-900 mb-1">
                {getPipelineCount('qualified')}
              </div>
              <div className="text-sm text-blue-600">Qualifié</div>
            </div>

            <div
              className="text-center p-4 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors"
              onClick={() => navigate('/admin/contact-requests?status=assigned')}
            >
              <div className="text-3xl font-bold text-purple-900 mb-1">
                {getPipelineCount('assigned')}
              </div>
              <div className="text-sm text-purple-600">Assigné</div>
            </div>

            <div
              className="text-center p-4 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors"
              onClick={() => navigate('/admin/contact-requests?status=visit_planned')}
            >
              <div className="text-3xl font-bold text-yellow-900 mb-1">
                {getPipelineCount('visit_planned')}
              </div>
              <div className="text-sm text-yellow-600">Visite</div>
            </div>

            <div
              className="text-center p-4 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
              onClick={() => navigate('/admin/contact-requests?status=completed')}
            >
              <div className="text-3xl font-bold text-green-900 mb-1">
                {getPipelineCount('completed')}
              </div>
              <div className="text-sm text-green-600">Converti</div>
            </div>

            <div
              className="text-center p-4 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => navigate('/admin/contact-requests?status=lost')}
            >
              <div className="text-3xl font-bold text-red-900 mb-1">
                {getPipelineCount('lost')}
              </div>
              <div className="text-sm text-red-600">Perdu</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default AdminDashboard;
